import { useEffect } from "react";

interface UseReminderProps {
  remindTime: Date | null;
  onTriggerEvent?: () => void;
}

const _MAX_DELAY = 2147483647;

/**
 * @description Schedules a single setTimeout that fires `onTriggerEvent`
 *   when `remindTime` arrives. Re-arms whenever `remindTime` changes,
 *   so callers can swap in a sooner appointment without unmounting.
 *
 * @author Rajkumar Rathod
 */
export const useReminder = ({
  remindTime,
  onTriggerEvent,
}: UseReminderProps) => {
  const remindAt = remindTime?.getTime() ?? null;

  useEffect(() => {
    if (remindAt === null) return;

    const timeDifference = remindAt - Date.now();

    if (timeDifference <= 0) {
      return;
    }

    if (timeDifference > _MAX_DELAY) {
      return;
    }

    const timer = setTimeout(() => {
      onTriggerEvent?.();
    }, timeDifference);

    return () => clearTimeout(timer);
  }, [remindAt]);
};
